import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, User, Clock, ArrowRight, Trash2, PlusCircle } from 'lucide-react';
import { getContactChangelog } from '../../../api/contact';

// Pretty labels for the audited fields (must match _AUDITED_CONTACT_FIELDS on the backend).
const FIELD_LABELS = {
  name: 'Full Name',
  first_name: 'First Name',
  last_name: 'Last Name',
  preferred_name: 'Preferred Name',
  company: 'Company',
  position: 'Position',
  email: 'Email',
  additional_emails: 'Secondary Emails',
  phone: 'Mobile Phone',
  alternate_phone: 'Work / Alternate Phone',
  address: 'Address',
  city: 'City',
  state: 'State',
  postal_code: 'Postal Code',
  country: 'Country',
  birthday: 'Birthday',
  contact_type: 'Contact Type',
  source: 'Source',
  tags: 'Tags',
  notes: 'Notes',
  preferred_contact_method: 'Contact Method',
  do_not_contact: 'Unsubscribed',
  email_opt_in: 'Email Marketing',
  sms_opt_in: 'SMS Marketing',
  is_buyer_advocate: 'Buyer Advocate',
  agent: 'Assigned Agent',
};

const fieldLabel = (key) =>
  FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Render a stored value for display in the diff.
const displayValue = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(empty)';
  return String(v);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const ACTION_META = {
  created: { label: 'Created', Icon: PlusCircle, color: 'text-emerald-500 bg-emerald-50' },
  updated: { label: 'Updated', Icon: User, color: 'text-[#55C5D0] bg-cyan-50' },
  archived: { label: 'Deleted', Icon: Trash2, color: 'text-red-500 bg-red-50' },
  restored: { label: 'Restored', Icon: PlusCircle, color: 'text-emerald-500 bg-emerald-50' },
};

const ContactChangeLog = ({ contactId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getContactChangelog(contactId);
        if (!cancelled) setLogs(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 403
              ? 'Only admins can view change history.'
              : 'Failed to load change history.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contactId]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 flex items-center justify-center gap-2 text-sm font-bold text-red-500">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-center">
        <Clock className="w-6 h-6 text-gray-300 mb-2" />
        <p className="text-sm font-bold text-gray-400">No changes recorded yet.</p>
        <p className="text-xs text-gray-400 mt-1">Edits made from now on will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {logs.map((log) => {
        const meta = ACTION_META[log.action] || ACTION_META.updated;
        const { Icon } = meta;
        const changes = log.changes && typeof log.changes === 'object' ? log.changes : {};
        return (
          <div key={log.id} className="border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {meta.label} by {log.user_name || 'System'}
                  </p>
                  <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtDate(log.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {Object.keys(changes).length > 0 && (
              <div className="mt-3 pl-10 space-y-1.5">
                {Object.entries(changes).map(([field, diff]) => (
                  <div key={field} className="text-[13px] flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-600">{fieldLabel(field)}:</span>
                    <span className="px-2 py-0.5 rounded-md bg-red-50 text-red-600 line-through decoration-red-300">
                      {displayValue(diff?.old)}
                    </span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700">
                      {displayValue(diff?.new)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(changes).length === 0 && log.description && (
              <p className="mt-2 pl-10 text-[13px] text-gray-500">{log.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ContactChangeLog;
